import type { CVData, CVTemplate } from "@/lib/cv-templates";
import logoAsset from "@/assets/logo.jpg.asset.json";

type Props = { data: CVData; template: CVTemplate };


const accentToHex: Record<string, string> = {
  "bg-slate-700": "#334155",
  "bg-slate-800": "#1e293b",
  "bg-emerald-600": "#059669",
  "bg-emerald-700": "#047857",
  "bg-teal-600": "#0d9488",
  "bg-navy": "#0b1e3f",
  "bg-amber-700": "#b45309",
  "bg-indigo-700": "#4338ca",
  "bg-violet-700": "#6d28d9",
  "bg-sky-700": "#0369a1",
  "bg-rose-600": "#e11d48",
  "bg-rose-700": "#be123c",
  "bg-orange-600": "#ea580c",
  "bg-zinc-800": "#27272a",
  "bg-stone-700": "#44403c",
};

export function accentHexFor(template: CVTemplate) {
  return accentToHex[template.accent] ?? "#0b1e3f";
}


export function CVPreview({ data, template }: Props) {
  const accent = accentToHex[template.accent] ?? "#0b1e3f";
  const fontClass =
    template.font === "serif"
      ? "font-serif"
      : template.font === "mono"
        ? "font-mono"
        : "font-sans";

  return (
    <div
      id="cv-print"
      className={`bg-white text-[#111] ${fontClass} mx-auto w-full max-w-[820px] p-10 text-justify shadow-soft [text-wrap:pretty] print:shadow-none`}
      style={{ minHeight: "1120px" }}
    >
      {template.layout === "split" ? (
        <SplitLayout data={data} accent={accent} />
      ) : template.layout === "executive" ? (
        <ExecutiveLayout data={data} accent={accent} />
      ) : template.layout === "minimal" ? (
        <MinimalLayout data={data} accent={accent} />
      ) : template.layout === "creative" ? (
        <CreativeLayout data={data} accent={accent} />
      ) : (
        <ClassicLayout data={data} accent={accent} />
      )}
      <BrandFooter accent={accent} />
    </div>
  );
}

function BrandFooter({ accent }: { accent: string }) {
  return (
    <div
      className="mt-8 flex items-center justify-between gap-3 border-t pt-3 text-[10px] text-gray-500"
      style={{ borderColor: accent }}
    >
      <div className="flex items-center gap-2">
        <img
          src={logoAsset.url}
          alt="Nkadimeng Danny Legal & Compliance Support"
          className="h-7 w-7 rounded-sm object-cover"
        />
        <span>
          Prepared with <strong>Nkadimeng Danny Legal &amp; Compliance Support</strong>
        </span>
      </div>
      <span>nkadimengdannylegalcompliance.lovable.app</span>
    </div>
  );
}


function ClassicLayout({ data, accent }: { data: CVData; accent: string }) {
  return (
    <>
      <header className="border-b-2 pb-3" style={{ borderColor: accent }}>
        <h1 className="text-3xl font-bold" style={{ color: accent }}>
          {data.fullName || "Your Name"}
        </h1>
        <p className="mt-1 text-sm text-gray-700">{data.headline}</p>
        <p className="mt-2 text-xs text-gray-600">
          {[data.email, data.phone, data.location].filter(Boolean).join("  •  ")}
        </p>
      </header>
      <Section title="Profile" accent={accent}>
        <p className="text-sm leading-relaxed">{data.summary}</p>
      </Section>
      <Section title="Experience" accent={accent}>
        <ExperienceList data={data} />
      </Section>
      <Section title="Education" accent={accent}>
        <EducationList data={data} />
      </Section>
      {data.skills.length > 0 && (
        <Section title="Skills" accent={accent}>
          <p className="text-sm">{data.skills.join(" • ")}</p>
        </Section>
      )}
      <Footer data={data} accent={accent} />
    </>
  );
}

function MinimalLayout({ data, accent }: { data: CVData; accent: string }) {
  return (
    <>
      <header>
        <h1 className="text-4xl font-light tracking-tight">{data.fullName}</h1>
        <div
          className="mt-1 h-0.5 w-16"
          style={{ background: accent }}
          aria-hidden
        />
        <p className="mt-3 text-sm uppercase tracking-[0.2em] text-gray-600">
          {data.headline}
        </p>
        <p className="mt-2 text-xs text-gray-600">
          {[data.email, data.phone, data.location].filter(Boolean).join("  ·  ")}
        </p>
      </header>
      <Section title="Summary" accent={accent} uppercase>
        <p className="text-sm leading-relaxed">{data.summary}</p>
      </Section>
      <Section title="Experience" accent={accent} uppercase>
        <ExperienceList data={data} />
      </Section>
      <Section title="Education" accent={accent} uppercase>
        <EducationList data={data} />
      </Section>
      {data.skills.length > 0 && (
        <Section title="Skills" accent={accent} uppercase>
          <ul className="grid grid-cols-2 gap-1 text-sm">
            {data.skills.map((s) => (
              <li key={s}>— {s}</li>
            ))}
          </ul>
        </Section>
      )}
      <Footer data={data} accent={accent} />
    </>
  );
}

function SplitLayout({ data, accent }: { data: CVData; accent: string }) {
  return (
    <div className="grid grid-cols-[1fr_2fr] gap-6">
      <aside className="space-y-5 rounded p-5 text-white" style={{ background: accent }}>
        <div>
          <h1 className="text-2xl font-bold leading-tight">{data.fullName}</h1>
          <p className="mt-1 text-xs opacity-90">{data.headline}</p>
        </div>
        <div className="text-xs space-y-1">
          {data.email && <p>{data.email}</p>}
          {data.phone && <p>{data.phone}</p>}
          {data.location && <p>{data.location}</p>}
          {data.links?.map((l) => (
            <p key={l.url}>{l.label}: {l.url}</p>
          ))}
        </div>
        {data.skills.length > 0 && (
          <div>
            <h2 className="text-xs uppercase tracking-widest opacity-80">Skills</h2>
            <ul className="mt-2 space-y-1 text-xs">
              {data.skills.map((s) => <li key={s}>• {s}</li>)}
            </ul>
          </div>
        )}
        {data.languages && data.languages.length > 0 && (
          <div>
            <h2 className="text-xs uppercase tracking-widest opacity-80">Languages</h2>
            <p className="mt-2 text-xs">{data.languages.join(", ")}</p>
          </div>
        )}
      </aside>
      <main>
        <Section title="Profile" accent={accent}>
          <p className="text-sm leading-relaxed">{data.summary}</p>
        </Section>
        <Section title="Experience" accent={accent}>
          <ExperienceList data={data} />
        </Section>
        <Section title="Education" accent={accent}>
          <EducationList data={data} />
        </Section>
        {data.certifications && data.certifications.length > 0 && (
          <Section title="Certifications" accent={accent}>
            <ul className="text-sm space-y-1">
              {data.certifications.map((c) => (
                <li key={c.name}>
                  <strong>{c.name}</strong> — {c.issuer}, {c.year}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </main>
    </div>
  );
}

function ExecutiveLayout({ data, accent }: { data: CVData; accent: string }) {
  return (
    <>
      <header className="text-center">
        <h1 className="text-4xl font-bold uppercase tracking-wide" style={{ color: accent }}>
          {data.fullName}
        </h1>
        <p className="mt-1 text-sm tracking-widest text-gray-700">{data.headline}</p>
        <div className="mx-auto mt-3 h-px w-2/3" style={{ background: accent }} />
        <p className="mt-2 text-xs text-gray-600">
          {[data.email, data.phone, data.location].filter(Boolean).join("  |  ")}
        </p>
      </header>
      <Section title="Executive Summary" accent={accent} center>
        <p className="text-sm leading-relaxed">{data.summary}</p>
      </Section>
      <Section title="Professional Experience" accent={accent} center>
        <ExperienceList data={data} />
      </Section>
      <div className="grid grid-cols-2 gap-6">
        <Section title="Education" accent={accent}>
          <EducationList data={data} />
        </Section>
        {data.skills.length > 0 && (
          <Section title="Core Competencies" accent={accent}>
            <ul className="grid grid-cols-1 gap-1 text-sm">
              {data.skills.map((s) => (
                <li key={s}>‣ {s}</li>
              ))}
            </ul>
          </Section>
        )}
      </div>
      <Footer data={data} accent={accent} />
    </>
  );
}

function CreativeLayout({ data, accent }: { data: CVData; accent: string }) {
  return (
    <>
      <header
        className="-mx-10 -mt-10 mb-6 px-10 py-8 text-white"
        style={{ background: accent }}
      >
        <h1 className="text-4xl font-extrabold">{data.fullName}</h1>
        <p className="mt-1 text-sm opacity-90">{data.headline}</p>
        <p className="mt-3 text-xs opacity-80">
          {[data.email, data.phone, data.location].filter(Boolean).join("  •  ")}
        </p>
      </header>
      <Section title="About" accent={accent}>
        <p className="text-sm leading-relaxed">{data.summary}</p>
      </Section>
      <Section title="Experience" accent={accent}>
        <ExperienceList data={data} />
      </Section>
      <div className="grid grid-cols-2 gap-6">
        <Section title="Education" accent={accent}>
          <EducationList data={data} />
        </Section>
        {data.skills.length > 0 && (
          <Section title="Skills" accent={accent}>
            <div className="flex flex-wrap gap-1.5">
              {data.skills.map((s) => (
                <span
                  key={s}
                  className="rounded-full border px-2.5 py-0.5 text-xs"
                  style={{ borderColor: accent, color: accent }}
                >
                  {s}
                </span>
              ))}
            </div>
          </Section>
        )}
      </div>
      <Footer data={data} accent={accent} />
    </>
  );
}

function Section({
  title,
  accent,
  uppercase,
  center,
  children,
}: {
  title: string;
  accent: string;
  uppercase?: boolean;
  center?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-5">
      <h2
        className={`mb-2 text-sm font-bold ${uppercase ? "uppercase tracking-[0.2em]" : ""} ${center ? "text-center" : ""}`}
        style={{ color: accent }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function ExperienceList({ data }: { data: CVData }) {
  return (
    <ul className="space-y-3">
      {data.experience.map((e, i) => (
        <li key={i}>
          <div className="flex flex-wrap justify-between gap-2">
            <p className="text-sm font-semibold">
              {e.role} — <span className="font-normal">{e.company}</span>
              {e.location && <span className="text-gray-600"> · {e.location}</span>}
            </p>
            <p className="text-xs text-gray-600">
              {e.start} – {e.end}
            </p>
          </div>
          <ul className="mt-1 list-disc pl-5 text-sm">
            {e.bullets.map((b, j) => (
              <li key={j}>{b}</li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function EducationList({ data }: { data: CVData }) {
  return (
    <ul className="space-y-2">
      {data.education.map((ed, i) => (
        <li key={i} className="text-sm">
          <p className="font-semibold">{ed.qualification}</p>
          <p className="text-gray-700">
            {ed.institution} · {ed.year}
          </p>
          {ed.notes && <p className="text-xs text-gray-600">{ed.notes}</p>}
        </li>
      ))}
    </ul>
  );
}

function Footer({ data, accent }: { data: CVData; accent: string }) {
  const hasAchievements = data.achievements && data.achievements.length > 0;
  const hasAwards = data.awards && data.awards.length > 0;
  const hasCerts = data.certifications && data.certifications.length > 0;
  const hasLangs = data.languages && data.languages.length > 0;
  if (!data.references && !hasAchievements && !hasAwards && !hasCerts && !hasLangs) return null;
  return (
    <>
      {hasAchievements && (
        <Section title="Key Achievements" accent={accent}>
          <ul className="space-y-1 text-sm">
            {data.achievements!.map((a, i) => (
              <li key={i}>• {a}</li>
            ))}
          </ul>
        </Section>
      )}
      {hasAwards && (
        <Section title="Awards & Honours" accent={accent}>
          <ul className="space-y-1 text-sm">
            {data.awards!.map((a, i) => (
              <li key={i}>
                • <strong>{a.title}</strong>
                {a.issuer ? ` — ${a.issuer}` : ""}
                {a.year ? ` (${a.year})` : ""}
                {a.detail ? `. ${a.detail}` : ""}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {hasCerts && (
        <Section title="Certifications" accent={accent}>
          <ul className="text-sm space-y-1">
            {data.certifications!.map((c) => (
              <li key={c.name}>
                <strong>{c.name}</strong> — {c.issuer}, {c.year}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {(hasLangs || data.references) && (
        <Section title="Additional" accent={accent}>
          {hasLangs && (
            <p className="text-sm">
              <strong>Languages:</strong> {data.languages!.join(", ")}
            </p>
          )}
          {data.references && (
            <p className="text-sm">
              <strong>References:</strong> {data.references}
            </p>
          )}
        </Section>
      )}
    </>
  );
}


/**
 * MiniPreview — a small, non-interactive thumbnail rendering of the template
 * so users see real structure (header, columns, lines, accent bar) instead of
 * a generic color band. Pure CSS, no real CV data needed.
 */
export function MiniPreview({ template }: { template: CVTemplate }) {
  const accent = accentHexFor(template);
  const layout = template.layout;

  const headerBar = (
    <div className="flex items-center justify-between gap-2">
      <div className="space-y-1">
        <div className="h-1.5 w-16 rounded-sm" style={{ background: accent }} />
        <div className="h-1 w-10 rounded-sm bg-gray-300" />
      </div>
      <div className="h-1 w-12 rounded-sm bg-gray-200" />
    </div>
  );

  const lines = (n = 4, w = "w-full") => (
    <div className="space-y-[3px]">
      {Array.from({ length: n }).map((_, i) => (
        <div
          key={i}
          className={`h-[3px] rounded-sm bg-gray-200 ${i === n - 1 ? "w-2/3" : w}`}
        />
      ))}
    </div>
  );

  const sectionHeading = (
    <div
      className="mt-2 h-[5px] w-12 rounded-sm"
      style={{ background: accent, opacity: 0.85 }}
    />
  );

  if (layout === "split") {
    return (
      <div className="grid h-32 grid-cols-[40%_60%] gap-1 overflow-hidden rounded-sm bg-white p-2 shadow-inner ring-1 ring-gray-200">
        <div
          className="space-y-2 rounded-sm p-2 text-white"
          style={{ background: accent }}
        >
          <div className="h-2 w-3/4 rounded-sm bg-white/80" />
          <div className="h-1 w-1/2 rounded-sm bg-white/60" />
          <div className="space-y-1 pt-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-[3px] w-full rounded-sm bg-white/40" />
            ))}
          </div>
        </div>
        <div className="space-y-1.5 p-1">
          {sectionHeading}
          {lines(3)}
          {sectionHeading}
          {lines(3)}
        </div>
      </div>
    );
  }

  if (layout === "executive") {
    return (
      <div className="h-32 space-y-2 overflow-hidden rounded-sm bg-white p-3 text-center shadow-inner ring-1 ring-gray-200">
        <div className="mx-auto h-2 w-1/2 rounded-sm" style={{ background: accent }} />
        <div className="mx-auto h-1 w-1/3 rounded-sm bg-gray-300" />
        <div className="mx-auto h-[1px] w-3/4" style={{ background: accent }} />
        <div className="space-y-1.5 pt-1 text-left">
          {sectionHeading}
          {lines(3)}
        </div>
      </div>
    );
  }

  if (layout === "creative") {
    return (
      <div className="h-32 overflow-hidden rounded-sm bg-white shadow-inner ring-1 ring-gray-200">
        <div className="p-2 text-white" style={{ background: accent }}>
          <div className="h-2 w-3/4 rounded-sm bg-white/85" />
          <div className="mt-1 h-1 w-1/2 rounded-sm bg-white/60" />
        </div>
        <div className="space-y-1.5 p-2">
          {sectionHeading}
          {lines(3)}
          <div className="flex flex-wrap gap-1 pt-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <span
                key={i}
                className="inline-block h-2 w-6 rounded-full border"
                style={{ borderColor: accent }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (layout === "minimal") {
    return (
      <div className="h-32 space-y-2 overflow-hidden rounded-sm bg-white p-3 shadow-inner ring-1 ring-gray-200">
        <div className="h-2 w-1/2 rounded-sm bg-gray-700" />
        <div className="h-[2px] w-8 rounded-sm" style={{ background: accent }} />
        <div className="h-1 w-1/3 rounded-sm bg-gray-300" />
        <div className="space-y-1 pt-1">
          {sectionHeading}
          {lines(3)}
        </div>
      </div>
    );
  }

  if (layout === "modern") {
    return (
      <div className="h-32 space-y-2 overflow-hidden rounded-sm bg-white p-2 shadow-inner ring-1 ring-gray-200">
        <div
          className="flex items-center justify-between rounded-sm px-2 py-1.5 text-white"
          style={{ background: accent }}
        >
          <div className="h-1.5 w-16 rounded-sm bg-white/85" />
          <div className="h-1 w-10 rounded-sm bg-white/60" />
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <div className="space-y-1">
            {sectionHeading}
            {lines(3)}
          </div>
          <div className="col-span-2 space-y-1">
            {sectionHeading}
            {lines(4)}
          </div>
        </div>
      </div>
    );
  }

  // classic / default
  return (
    <div className="h-32 space-y-1.5 overflow-hidden rounded-sm bg-white p-3 shadow-inner ring-1 ring-gray-200">
      {headerBar}
      <div className="border-b" style={{ borderColor: accent }} />
      {sectionHeading}
      {lines(3)}
      {sectionHeading}
      {lines(3)}
    </div>
  );
}

